import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
  FaSearch,
  FaFilter,
  FaEdit,
  FaTrash,
  FaFileContract,
  FaCalendarAlt,
  FaMoneyBillWave,
  FaReceipt,
  FaCheckCircle,
  FaTimesCircle,
  FaClock,
  FaBuilding,
  FaPlus,
  FaEye,
  FaDownload,
  FaUserCheck,
  FaSync,
  FaFilePdf,
  FaHotel,
  FaUtensils,
  FaBan,
  FaPercentage,
  FaThumbsUp,
  FaThumbsDown,
  FaList,
  FaChevronRight,
  FaChevronLeft,
} from 'react-icons/fa';

const BQ_BASE_URL = process.env.REACT_APP_BQ_BASE_URL;

const CorporateContractsPage = () => {
  const navigate = useNavigate();
  const [contracts, setContracts] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [selectedContract, setSelectedContract] = useState(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [activeTab, setActiveTab] = useState('all');
  const [creatingContract, setCreatingContract] = useState(false);

  const itemsPerPage = 8;

  // Enhanced contract form state matching API structure
  const [newContract, setNewContract] = useState({
    // Step 1: Basic Information
    companyId: '',
    contractId: '',
    contractname: '',
    contractDescription: '',
    gst_number: '',

    // Step 2: Contract Duration
    contractStartDate: '',
    contractEndDate: '',
    contractDuration: '',
    autoRenewal: false,
    renewalNoticePeriod: 30,

    // Step 3: Terms & Conditions
    termsAndConditions: `This Corporate Agreement ("Agreement") is made between Pagoda Executive and [Company Name] effective from the contract start date.

STANDARD TERMS & CONDITIONS:

1. ROOM RATES & AVAILABILITY
   - Corporate rates are applicable for direct reservations only
   - Rates are exclusive of applicable taxes and service charges
   - Subject to availability at time of reservation

2. RESERVATIONS & CANCELLATIONS
   - Reservations must be guaranteed by credit card or company guarantee
   - Cancellations accepted up to 48 hours prior to arrival without penalty
   - No-shows will be charged one night's room rate

3. PAYMENT TERMS
   - Invoices payable within 30 days of receipt
   - Late payments subject to interest charges of 1% per month
   - Company agrees to maintain valid credit card on file

4. CHECK-IN/CHECK-OUT
   - Standard check-in: 11:00 AM | Check-out: 12:00 PM
   - Early check-in and late check-out subject to availability and additional charges

5. LIABILITY & INSURANCE
   - Company liable for any damages caused by its employees
   - Hotel not responsible for loss or theft of personal belongings
   - Force majeure events may affect contract obligations

6. CONFIDENTIALITY
   - Both parties agree to maintain confidentiality of commercial terms
   - Customer data handled in accordance with privacy laws

7. GOVERNING LAW
   - This Agreement shall be governed by the laws of MAHARASHTRA/INDIA
   - Disputes to be resolved through arbitration in CHIPLUN

By signing this agreement, both parties acknowledge and accept these terms and conditions.`,

    // Step 4: Payment Terms
    paymentTerms: {
      billingCycle: 'monthly',
      paymentTerms: 'net30',
      latePaymentFee: 0,
      creditlimit: 0,
      // baseprice: 0,
      currency: 'INR',
      invoicingDate: '1',
      paymentDueDays: 30,
    },

    // Step 5: Withdrawal Terms
    withdrawalTerms: {
      withdrawalNotice: '30',
      withdrawalPenalty: '',
      forceMajeure: '',
      penaltyApplicable: false,
    },

    // Step 6: Discount Structure
    discountStructure: {
      discountType: 'flat',
      flatDiscount: 0,
      inclusiveDiscount: true,
      discountSlabs: [{ minAmount: 0, discountPercent: 0 }],
      nonApplicableConditions: [],
      nonInclusiveItems: [],
    },

    // Step 7: Contact Details
    contactDetails: {
      contactPerson: '',
      contactEmail: '',
      contactPhone: '',
    },

    // Step 8: Signatory Details
    signatoryDetails: {
      signatoryName: '',
      signatoryTitle: '',
      signatoryEmail: '',
      signatoryPhone: '',
    },
  });

  useEffect(() => {
    fetchContracts();
    fetchCompanies();
  }, []);

  // Toast configuration
  const showToast = (message, type = 'info') => {
    const toastConfig = {
      position: 'top-right',
      autoClose: 3000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
    };

    switch (type) {
      case 'success':
        toast.success(message, toastConfig);
        break;
      case 'error':
        toast.error(message, toastConfig);
        break;
      case 'warning':
        toast.warning(message, toastConfig);
        break;
      default:
        toast.info(message, toastConfig);
    }
  };

  const fetchContracts = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${BQ_BASE_URL || 'http://localhost:9000'}/bq/api/contracts/`, {
        method: 'GET',
        headers: {
          accept: 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (result.success && result.contracts) {
        let transformedContracts = result.contracts.map((contract) => {
          const now = new Date();
          const endDate = new Date(contract.contract_end);
          const isExpired = endDate < now;

          // Map backend status to frontend workflow steps
          const getWorkflowStep = (status) => {
            switch (status) {
              case 'draft':
                return 'sent_for_signature';
              case 'pending':
                return 'awaiting_signature';
              case 'ready_for_approval':
                return 'ready_for_approval';
              case 'approved':
                return 'approved';
              case 'rejected':
                return 'rejected';
              default:
                return 'sent_for_signature';
            }
          };

          return {
            contractid: contract.contractid,
            contractname: contract.contract_name,
            companyid: contract.companyid,
            contractStartDate: contract.contract_start,
            contractEndDate: contract.contract_end,
            status: contract.status,
            gst_number: contract.gst_number,
            createdDate: contract.created_date,
            updatedDate: contract.updated_date,
            createdBy: 'sales_user',
            approvalStatus: contract.status,
            workflowStep: getWorkflowStep(contract.status),
            emailSent: contract.sent_pdf_on_email,
            pdfUploaded: contract.pdf_uploaded,
            isExpired: isExpired,
            accessLink: contract.download_url,
            linkExpiresAt: null,
            linkGeneratedAt: null,
            contactDetails: {
              contactPerson: contract.contact_person,
              contactEmail: contract.contact_email,
              contactPhone: contract.contact_phone,
            },
            paymentTerms: {
              billingCycle: contract.billing_cycle || 'monthly',
              paymentTerms: contract.contract_discount_structure?.payment_terms || 'net30',
              latePaymentFee:
                parseFloat(contract.contract_discount_structure?.late_payment_fee) || 0,
              creditlimit: parseFloat(contract.contract_discount_structure?.credit_limit) || 0,
              baseprice: parseFloat(contract.contract_discount_structure?.base_price) || 0,
              currency: contract.contract_discount_structure?.currency || 'INR',
              invoicingDate: contract.contract_discount_structure?.invoicing_date || '1',
            },
            // Keep original data for reference
            contract_discount_structure: contract.contract_discount_structure,
            contract_discount_slab: contract.contract_discount_slab,
            discountStructure: {
              discountType: contract.contract_discount_structure?.discount_type || 'flat',
              flatDiscount: parseFloat(contract.contract_discount_structure?.flat_discount) || 0,
              inclusiveDiscount: contract.contract_discount_structure?.inclusive_discount || true,
              discountSlabs:
                contract.contract_discount_slab?.map((slab) => ({
                  minAmount: parseFloat(slab.min_amount) || 0,
                  discountPercent: parseFloat(slab.discount_percent) || 0,
                })) || [],
            },
            withdrawalTerms: {
              withdrawalNotice: contract.contract_duration?.withdrawal_notice || '30',
              withdrawalPenalty: contract.contract_duration?.withdrawal_penalty || '',
              forceMajeure: contract.contract_duration?.force_majeure || '',
              penaltyApplicable: !!contract.contract_duration?.withdrawal_penalty,
            },
            signatoryDetails: {
              signatoryName: contract.contract_signatory?.[0]?.signatory_name || '',
              signatoryTitle: contract.contract_signatory?.[0]?.signatory_title || '',
              signatoryEmail: contract.contract_signatory?.[0]?.signatory_email || '',
              signatoryPhone: contract.contract_signatory?.[0]?.signatory_phone || '',
            },
            termsAndConditions: contract.contract_terms?.contract_terms || '',
            autoRenewal: contract.contract_duration?.auto_renewal || false,
            renewalNoticePeriod: contract.contract_duration?.renewal_notice_period || 30,
            company: contract.company,
            // Backend status fields for reference
            approved_at: contract.approved_at,
            rejected_at: contract.rejected_at,
            rejection_reason: contract.rejection_reason,
            is_approved: contract.is_approved,
            is_rejected: contract.is_rejected,
            download_url: contract.download_url,
          };
        });

        setContracts(transformedContracts);
      } else {
        setContracts([]);
      }
    } catch (error) {
      console.error('Error fetching contracts:', error);
      toast.error('Error fetching contracts');
      setContracts([]);
    } finally {
      setLoading(false);
    }
  };

  // API call to fetch companies
  const fetchCompanies = async () => {
    try {
      const response = await fetch(`${BQ_BASE_URL || 'http://localhost:9000'}/bq/api/companies`, {
        method: 'GET',
        headers: {
          accept: 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      setCompanies(result.companies || []);
    } catch (error) {
      console.error('Error fetching companies:', error);
      showToast('Error fetching companies', 'error');
      setCompanies([]);
    }
  };

  // API call to create contract
  const createContractAPI = async (contractData) => {
    try {
      console.log('Making API call with companyId:', contractData.companyId);

      const response = await fetch(`${BQ_BASE_URL || 'http://localhost:9000'}/bq/api/contract/`, {
        method: 'POST',
        headers: {
          accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(contractData),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('API Error Response:', errorText);
        let errorDetail = `HTTP error! status: ${response.status}`;
        try {
          const errorData = JSON.parse(errorText);
          errorDetail = errorData.detail || errorDetail;
        } catch (e) {
          errorDetail = errorText || errorDetail;
        }
        throw new Error(errorDetail);
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Error creating contract:', error);
      throw error;
    }
  };

  // Enhanced Step Indicator Component
  const StepIndicator = ({ currentStep, steps }) => {
    return (
      <div className="flex items-center justify-between mb-8">
        {steps.map((step, index) => {
          const isCompleted = index + 1 < currentStep;
          const isCurrent = index + 1 === currentStep;
          const isUpcoming = index + 1 > currentStep;
          const IconComponent = step.icon;

          return (
            <React.Fragment key={step.number}>
              <div className="flex flex-col items-center flex-1">
                <div
                  className={`w-12 h-12 rounded-full flex items-center justify-center border-2 font-semibold text-lg transition-all duration-300 ${
                    isCompleted
                      ? 'bg-black border-black text-white shadow-lg'
                      : isCurrent
                        ? 'bg-gray-600 border-gray-600 text-white shadow-lg transform scale-110'
                        : 'bg-white border-gray-300 text-gray-400'
                  }`}
                >
                  {isCompleted ? (
                    <FaCheckCircle className="text-sm" />
                  ) : (
                    <IconComponent className="text-sm" />
                  )}
                </div>
                <span
                  className={`mt-2 text-sm font-medium text-center max-w-20 ${
                    isCurrent
                      ? 'text-black font-semibold'
                      : isCompleted
                        ? 'text-black'
                        : 'text-gray-400'
                  }`}
                >
                  {step.title}
                </span>
              </div>
              {index < steps.length - 1 && (
                <div
                  className={`flex-1 h-1 mx-2 transition-all duration-300 ${
                    isCompleted ? 'bg-black' : 'bg-gray-200'
                  }`}
                />
              )}
            </React.Fragment>
          );
        })}
      </div>
    );
  };

  const steps = [
    { number: 1, title: 'Basic Info', icon: FaBuilding },
    { number: 2, title: 'Duration', icon: FaCalendarAlt },
    { number: 3, title: 'Terms', icon: FaFileContract },
    { number: 4, title: 'Payment', icon: FaMoneyBillWave },
    { number: 5, title: 'Withdrawal', icon: FaBan },
    { number: 6, title: 'Discounts', icon: FaPercentage },
    { number: 7, title: 'Contact', icon: FaUserCheck },
    { number: 8, title: 'Review', icon: FaCheckCircle },
  ];

  // Validation functions
  const validateStep = (step) => {
    switch (step) {
      case 1:
        if (!newContract.companyId) {
          showToast('Please select a company', 'error');
          return false;
        }
        if (!newContract.contractname?.trim()) {
          showToast('Please enter a contract name', 'error');
          return false;
        }
        if (
          newContract.gst_number &&
          !/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/.test(newContract.gst_number)
        ) {
          showToast('Please enter a valid GST number', 'error');
          return false;
        }
        return true;

      // Updated validation for Step 2
      case 2:
        if (!newContract.contractStartDate) {
          showToast('Please select a start date', 'error');
          return false;
        }
        if (!newContract.contractEndDate) {
          showToast('Please select an end date', 'error');
          return false;
        }

        const startDate = new Date(newContract.contractStartDate);
        const endDate = new Date(newContract.contractEndDate);
        const today = new Date();
        today.setHours(0, 0, 0, 0); // Reset time part for accurate comparison

        // Check if end date is in the past
        if (endDate < today) {
          showToast('End date cannot be in the past', 'error');
          return false;
        }

        // Check if end date is same as start date
        if (startDate.getTime() === endDate.getTime()) {
          showToast('Start date and end date cannot be the same', 'error');
          return false;
        }

        // Check if end date is before start date
        if (endDate <= startDate) {
          showToast('End date must be after start date', 'error');
          return false;
        }

        return true;

      case 3:
        return (
          <div className="space-y-6">
            <div className="flex items-center space-x-3">
              <FaFileContract className="text-black text-xl" />
              <h3 className="text-lg font-semibold text-black">Terms & Conditions</h3>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-black mb-1">
                  Default Terms and Conditions *
                </label>
                <textarea
                  name="termsAndConditions"
                  value={newContract.termsAndConditions}
                  onChange={handleInputChange}
                  rows="6"
                  className="w-full p-3 border border-gray-300 rounded-lg bg-white text-black"
                  placeholder="Enter the terms and conditions for this contract..."
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  Default terms include standard hotel policies, cancellation terms, and corporate
                  agreement clauses.
                </p>
              </div>

              {/* Special Conditions Box */}
              <div className="mt-6 p-4 border-2 border-dashed border-gray-300 rounded-lg bg-gray-50">
                <div className="flex items-center space-x-2 mb-3">
                  <FaPlus className="text-gray-600" />
                  <h4 className="text-sm font-medium text-gray-700">
                    Special Conditions (Optional)
                  </h4>
                </div>
                <p className="text-xs text-gray-500 mb-3">
                  Add any special conditions that are not covered in the standard terms. These will
                  be included in the contract PDF.
                </p>
                <textarea
                  placeholder="Enter special conditions here... (e.g., Custom cancellation policies, special event terms, additional services, etc.)"
                  rows="4"
                  className="w-full p-3 border border-gray-300 rounded-lg bg-white text-black text-sm"
                  onChange={(e) => {
                    // This is a dummy field that would be sent to your terms and conditions in a real implementation
                    console.log('Special conditions:', e.target.value);
                    // In a real implementation, you might want to append this to the main termsAndConditions
                    // or store it separately in your state
                  }}
                />
                <p className="text-xs text-gray-400 mt-2">
                  Note: This special condition box is for demonstration purposes and will be
                  integrated with the terms and conditions API in the final implementation.
                </p>
              </div>

              {/* Default Terms Preview */}
              <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <h5 className="text-sm font-medium text-blue-800 mb-2">Default Terms Include:</h5>
                <ul className="text-xs text-blue-700 list-disc list-inside space-y-1">
                  <li>Standard corporate room rates and availability</li>
                  <li>Cancellation policy: 48 hours prior to arrival</li>
                  <li>Early check-in and late check-out terms</li>
                  <li>Meeting room booking policies</li>
                  <li>Food and beverage minimums for events</li>
                  <li>Payment terms and credit arrangements</li>
                  <li>Force majeure and liability clauses</li>
                </ul>
              </div>
            </div>
          </div>
        );

      case 4:
        // if (!newContract.paymentTerms.baseprice || parseFloat(newContract.paymentTerms.baseprice) <= 0) {
        //   showToast('Please enter a valid base price', 'error');
        //   return false;
        // }
        if (
          newContract.paymentTerms.creditlimit &&
          parseFloat(newContract.paymentTerms.creditlimit) < 0
        ) {
          showToast('Credit limit cannot be negative', 'error');
          return false;
        }
        return true;

      case 5:
        if (
          newContract.withdrawalTerms.penaltyApplicable &&
          !newContract.withdrawalTerms.withdrawalPenalty?.trim()
        ) {
          showToast('Please specify withdrawal penalty', 'error');
          return false;
        }
        return true;

      case 6:
        if (newContract.discountStructure.discountType === 'flat') {
          const flatDiscount = parseFloat(newContract.discountStructure.flatDiscount);
          if (isNaN(flatDiscount) || flatDiscount < 0) {
            showToast('Please enter a valid discount amount in rupees (₹)', 'error');
            return false;
          }
        } else {
          // Validate discount slabs (percentage-based)
          for (let i = 0; i < newContract.discountStructure.discountSlabs.length; i++) {
            const slab = newContract.discountStructure.discountSlabs[i];
            const minAmount = parseFloat(slab.minAmount);
            const discountPercent = parseFloat(slab.discountPercent);

            if (isNaN(minAmount) || minAmount < 0) {
              showToast(`Please enter valid minimum amount for slab ${i + 1}`, 'error');
              return false;
            }
            if (isNaN(discountPercent) || discountPercent < 0 || discountPercent > 100) {
              showToast(
                `Please enter valid discount percentage (0-100) for slab ${i + 1}`,
                'error'
              );
              return false;
            }
          }
        }
        return true;

      case 7:
        if (!newContract.contactDetails.contactPerson?.trim()) {
          showToast('Please enter contact person name', 'error');
          return false;
        }
        if (!newContract.contactDetails.contactEmail?.trim()) {
          showToast('Please enter contact email', 'error');
          return false;
        }
        if (!/\S+@\S+\.\S+/.test(newContract.contactDetails.contactEmail)) {
          showToast('Please enter a valid email address', 'error');
          return false;
        }
        if (!newContract.signatoryDetails.signatoryName?.trim()) {
          showToast('Please enter signatory name', 'error');
          return false;
        }
        if (!newContract.signatoryDetails.signatoryTitle?.trim()) {
          showToast('Please enter signatory title', 'error');
          return false;
        }
        if (!newContract.signatoryDetails.signatoryEmail?.trim()) {
          showToast('Please enter signatory email', 'error');
          return false;
        }
        if (!/\S+@\S+\.\S+/.test(newContract.signatoryDetails.signatoryEmail)) {
          showToast('Please enter a valid signatory email address', 'error');
          return false;
        }
        return true;

      case 8:
        // Final validation before submission
        return (
          validateStep(1) &&
          validateStep(2) &&
          validateStep(3) &&
          validateStep(4) &&
          validateStep(5) &&
          validateStep(6) &&
          validateStep(7)
        );

      default:
        return true;
    }
  };

  const nextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep((prev) => prev + 1);
    }
  };

  const prevStep = () => setCurrentStep((prev) => prev - 1);

  // Toggle Component
  const Toggle = ({ isOn, onToggle, title, description, name }) => (
    <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
      <div className="flex-1">
        <h4 className="text-sm font-medium text-black mb-1">{title}</h4>
        {description && <p className="text-xs text-gray-600">{description}</p>}
      </div>
      <button
        type="button"
        name={name}
        onClick={() => onToggle(!isOn)}
        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-2 ${
          isOn ? 'bg-black' : 'bg-gray-200'
        }`}
      >
        <span
          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
            isOn ? 'translate-x-6' : 'translate-x-1'
          }`}
        />
      </button>
    </div>
  );

  const calculateContractDuration = (startDate, endDate) => {
    if (!startDate || !endDate) return '0 months';
    const start = new Date(startDate);
    const end = new Date(endDate);
    const months =
      (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth());
    return `${months} months`;
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setNewContract((prev) => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: type === 'checkbox' ? checked : value,
        },
      }));
    } else {
      setNewContract((prev) => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value,
      }));
    }
  };

  // Handle toggle changes specifically
  const handleToggleChange = (name, value) => {
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setNewContract((prev) => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value,
        },
      }));
    } else {
      setNewContract((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  // Handle discount slab changes
  const handleDiscountSlabChange = (index, field, value) => {
    const updatedSlabs = [...newContract.discountStructure.discountSlabs];
    updatedSlabs[index][field] = value;
    setNewContract((prev) => ({
      ...prev,
      discountStructure: {
        ...prev.discountStructure,
        discountSlabs: updatedSlabs,
      },
    }));
  };

  const fetchSingleContract = async (contractId) => {
    try {
      const response = await fetch(
        `${BQ_BASE_URL || 'http://localhost:9000'}/bq/api/contract/${contractId}`,
        {
          method: 'GET',
          headers: {
            accept: 'application/json',
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (result.success) {
        return result.contract;
      } else {
        throw new Error(result.message || 'Failed to fetch contract');
      }
    } catch (error) {
      console.error('Error fetching contract:', error);
      throw error;
    }
  };

  // Download Contract PDF
  const handleDownloadContract = async (contract) => {
    try {
      if (contract.download_url) {
        // Use the download_url directly from the contract data
        window.open(contract.download_url, '_blank');
        toast.success('Contract PDF opened in new tab');
      } else {
        toast.warning('No contract PDF available for download');
      }
    } catch (error) {
      console.error('Error downloading contract:', error);
      toast.error('Error downloading contract PDF');
    }
  };

  // Generate Signed URL for PDF (you might need to implement this based on your backend)
  const generateSignedUrl = async (blobPath) => {
    try {
      // This is a placeholder - you'll need to implement based on your backend
      // You might have a separate endpoint to generate signed URLs
      const response = await fetch(
        `${BQ_BASE_URL || 'http://localhost:9000'}/bq/api/generate-signed-url`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ blob_path: blobPath }),
        }
      );

      if (response.ok) {
        const result = await response.json();
        return result.signed_url;
      }
      return null;
    } catch (error) {
      console.error('Error generating signed URL:', error);
      return null;
    }
  };

  // Enhanced View Contract Handler
  const handleViewContract = async (contract) => {
    try {
      setSelectedContract(contract);

      // Fetch latest contract data from API
      const latestContract = await fetchSingleContract(contract.contractid);

      // Transform the API response to match your frontend structure
      const transformedContract = transformContractData(latestContract);

      setSelectedContract(transformedContract);
      setShowViewModal(true);
    } catch (error) {
      console.error('Error fetching contract details:', error);
      showToast('Failed to load contract details', 'error');
      // Fallback to existing contract data
      setSelectedContract(contract);
      setShowViewModal(true);
    }
  };

  // Enhanced transform function to include PDF data
  const transformContractData = (contractData) => {
    const now = new Date();
    const endDate = new Date(contractData.contract_end);
    const isExpired = endDate < now;

    return {
      contractid: contractData.contractid,
      contractname: contractData.contract_name,
      companyid: contractData.companyid,
      contractStartDate: contractData.contract_start,
      contractEndDate: contractData.contract_end,
      status: contractData.status || 'active',
      gst_number: contractData.gst_number,
      createdDate: contractData.created_date,
      updatedDate: contractData.updated_date,
      createdBy: contractData.created_by || 'sales_user',
      // Status information from API
      approved_at: contractData.approved_at,
      approved_by: contractData.approved_by,
      rejected_at: contractData.rejected_at,
      rejected_by: contractData.rejected_by,
      rejection_reason: contractData.rejection_reason,
      is_approved: contractData.is_approved,
      is_rejected: contractData.is_rejected,
      contactDetails: {
        contactPerson: contractData.contact_person,
        contactEmail: contractData.contact_email,
        contactPhone: contractData.contact_phone,
      },
      paymentTerms: {
        billingCycle: contractData.billing_cycle || 'monthly',
        paymentTerms: contractData.contract_discount_structure?.payment_terms || 'net30',
        latePaymentFee: parseFloat(contractData.contract_discount_structure?.late_payment_fee) || 0,
        creditlimit: parseFloat(contractData.contract_discount_structure?.credit_limit) || 0,
        baseprice: parseFloat(contractData.contract_discount_structure?.base_price) || 0,
        currency: contractData.contract_discount_structure?.currency || 'INR',
        invoicingDate: contractData.contract_discount_structure?.invoicing_date || '1',
      },
      // Keep original data for reference
      contract_discount_structure: contractData.contract_discount_structure,
      contract_discount_slab: contractData.contract_discount_slab,
      discountStructure: {
        discountType: contractData.contract_discount_structure?.discount_type || 'flat',
        flatDiscount: parseFloat(contractData.contract_discount_structure?.flat_discount) || 0,
        inclusiveDiscount: contractData.contract_discount_structure?.inclusive_discount || true,
        discountSlabs:
          contractData.contract_discount_slab?.map((slab) => ({
            minAmount: parseFloat(slab.min_amount) || 0,
            discountPercent: parseFloat(slab.discount_percent) || 0,
          })) || [],
      },
      withdrawalTerms: {
        withdrawalNotice: contractData.contract_duration?.withdrawal_notice || '30',
        withdrawalPenalty: contractData.contract_duration?.withdrawal_penalty || '',
        forceMajeure: contractData.contract_duration?.force_majeure || '',
        penaltyApplicable: !!contractData.contract_duration?.withdrawal_penalty,
      },
      signatoryDetails: {
        signatoryName: contractData.contract_signatory?.[0]?.signatory_name || '',
        signatoryTitle: contractData.contract_signatory?.[0]?.signatory_title || '',
        signatoryEmail: contractData.contract_signatory?.[0]?.signatory_email || '',
        signatoryPhone: contractData.contract_signatory?.[0]?.signatory_phone || '',
      },
      termsAndConditions: contractData.contract_terms?.contract_terms || '',
      autoRenewal: contractData.contract_duration?.auto_renewal || false,
      renewalNoticePeriod: contractData.contract_duration?.renewal_notice_period || 30,
      company: contractData.company,
      // PDF information
      contractPdf: contractData.contract_discount_structure?.contract_pdf,
      hasPdf: !!contractData.contract_discount_structure?.contract_pdf,
      isSigned: contractData.contract_discount_structure?.contract_pdf?.includes('signed') || false,
      // Additional API fields
      contract_duration: contractData.contract_duration,
      contract_non_applicable_condition: contractData.contract_non_applicable_condition,
      contract_non_inclusive_item: contractData.contract_non_inclusive_item,
      contract_signatory: contractData.contract_signatory,
      contract_terms: contractData.contract_terms,
    };
  };

  const addDiscountSlab = () => {
    setNewContract((prev) => ({
      ...prev,
      discountStructure: {
        ...prev.discountStructure,
        discountSlabs: [
          ...prev.discountStructure.discountSlabs,
          { minAmount: 0, discountPercent: 0 },
        ],
      },
    }));
  };

  const removeDiscountSlab = (index) => {
    const updatedSlabs = newContract.discountStructure.discountSlabs.filter((_, i) => i !== index);
    setNewContract((prev) => ({
      ...prev,
      discountStructure: {
        ...prev.discountStructure,
        discountSlabs: updatedSlabs,
      },
    }));
  };

  // Handle non-applicable conditions
  const handleNonApplicableChange = (condition, checked) => {
    const updatedConditions = checked
      ? [...newContract.discountStructure.nonApplicableConditions, condition]
      : newContract.discountStructure.nonApplicableConditions.filter((c) => c !== condition);

    setNewContract((prev) => ({
      ...prev,
      discountStructure: {
        ...prev.discountStructure,
        nonApplicableConditions: updatedConditions,
      },
    }));
  };

  // Handle non-inclusive items
  const handleNonInclusiveChange = (item, checked) => {
    const updatedItems = checked
      ? [...newContract.discountStructure.nonInclusiveItems, item]
      : newContract.discountStructure.nonInclusiveItems.filter((i) => i !== item);

    setNewContract((prev) => ({
      ...prev,
      discountStructure: {
        ...prev.discountStructure,
        nonInclusiveItems: updatedItems,
      },
    }));
  };

  const handleCreateContract = async (e) => {
    e.preventDefault();

    if (!validateStep(8)) {
      return;
    }

    setCreatingContract(true);

    try {
      // Prepare the contract data with proper field mapping
      const contractData = {
        // Use 'companyId' as the contract API expects, but value from companyid
        companyId: newContract.companyId, // This comes from the company selection
        contractId: newContract.contractId,
        contractname: newContract.contractname,
        contractDescription: newContract.contractDescription,
        gst_number: newContract.gst_number,
        // Use proper ISO-8601 datetime format
        contractStartDate: newContract.contractStartDate
          ? `${newContract.contractStartDate}T00:00:00.000Z`
          : null,
        contractEndDate: newContract.contractEndDate
          ? `${newContract.contractEndDate}T23:59:59.999Z`
          : null,
        contractDuration: calculateContractDuration(
          newContract.contractStartDate,
          newContract.contractEndDate
        ),
        termsAndConditions: newContract.termsAndConditions,
        autoRenewal: newContract.autoRenewal,
        renewalNoticePeriod: newContract.renewalNoticePeriod,
        paymentTerms: {
          billingCycle: newContract.paymentTerms.billingCycle,
          paymentTerms: newContract.paymentTerms.paymentTerms,
          latePaymentFee: parseFloat(newContract.paymentTerms.latePaymentFee) || 0,
          creditlimit: parseFloat(newContract.paymentTerms.creditlimit) || 0,
          baseprice: parseFloat(newContract.paymentTerms.baseprice) || 0,
          currency: newContract.paymentTerms.currency,
          invoicingDate: newContract.paymentTerms.invoicingDate,
          paymentDueDays: parseInt(newContract.paymentTerms.paymentDueDays) || 30,
        },
        withdrawalTerms: {
          withdrawalNotice: newContract.withdrawalTerms.withdrawalNotice,
          withdrawalPenalty: newContract.withdrawalTerms.withdrawalPenalty,
          forceMajeure: newContract.withdrawalTerms.forceMajeure,
          penaltyApplicable: newContract.withdrawalTerms.penaltyApplicable,
        },
        discountStructure: {
          discountType: newContract.discountStructure.discountType,
          flatDiscount: parseFloat(newContract.discountStructure.flatDiscount) || 0,
          flatDiscountType:
            newContract.discountStructure.discountType === 'flat' ? 'amount' : 'percentage', // Add this
          inclusiveDiscount: newContract.discountStructure.inclusiveDiscount,
          discountSlabs: newContract.discountStructure.discountSlabs.map((slab) => ({
            minAmount: parseFloat(slab.minAmount) || 0,
            discountPercent: parseFloat(slab.discountPercent) || 0,
          })),
          nonApplicableConditions: newContract.discountStructure.nonApplicableConditions,
          nonInclusiveItems: newContract.discountStructure.nonInclusiveItems,
        },
        contactDetails: {
          contactPerson: newContract.contactDetails.contactPerson,
          contactEmail: newContract.contactDetails.contactEmail,
          contactPhone: newContract.contactDetails.contactPhone,
        },
        signatoryDetails: {
          signatoryName: newContract.signatoryDetails.signatoryName,
          signatoryTitle: newContract.signatoryDetails.signatoryTitle,
          signatoryEmail: newContract.signatoryDetails.signatoryEmail,
          signatoryPhone: newContract.signatoryDetails.signatoryPhone,
        },
        metadata: {
          status: 'draft',
          notes: 'Contract created via web portal',
          createdBy: 'admin_user',
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        },
      };

      console.log('Sending contract data to API:', contractData);

      const result = await createContractAPI(contractData);

      if (result.success) {
        // Add the new contract to local state
        const newContractWithId = {
          contractid: result.contract.contractid,
          contractname: result.contract.contractname,
          status: result.contract.status,
          createdDate: result.contract.createdDate,
          companyid: contractData.companyId, // Store as companyid in local state
          contractId: contractData.contractId,
          ...contractData,
        };

        setContracts((prev) => [...prev, newContractWithId]);
        setShowCreateModal(false);
        resetNewContract();
        setCurrentStep(1);
        showToast('Contract created successfully!', 'success');
      } else {
        throw new Error(result.message || 'Failed to create contract');
      }
    } catch (error) {
      console.error('Error creating contract:', error);
      showToast(`Error creating contract: ${error.message}`, 'error');
    } finally {
      setCreatingContract(false);
    }
  };

  const resetNewContract = () => {
    setNewContract({
      companyId: '',
      contractId: '',
      contractname: '',
      contractDescription: '',
      gst_number: '',
      contractStartDate: '',
      contractEndDate: '',
      contractDuration: '',
      autoRenewal: false,
      renewalNoticePeriod: 30,
      termsAndConditions: '',
      paymentTerms: {
        billingCycle: 'monthly',
        paymentTerms: 'net30',
        latePaymentFee: 0,
        creditlimit: 0,
        baseprice: 0,
        currency: 'INR',
        invoicingDate: '1',
        paymentDueDays: 30,
      },
      withdrawalTerms: {
        withdrawalNotice: '30',
        withdrawalPenalty: '',
        forceMajeure: '',
        penaltyApplicable: false,
      },
      discountStructure: {
        discountType: 'flat',
        flatDiscount: 0,
        inclusiveDiscount: true,
        discountSlabs: [{ minAmount: 0, discountPercent: 0 }],
        nonApplicableConditions: [],
        nonInclusiveItems: [],
      },
      contactDetails: {
        contactPerson: '',
        contactEmail: '',
        contactPhone: '',
      },
      signatoryDetails: {
        signatoryName: '',
        signatoryTitle: '',
        signatoryEmail: '',
        signatoryPhone: '',
      },
    });
  };

  // Hotel industry specific options
  const nonApplicableOptions = [
    'Diwali Season',
    'Christmas Season',
    'New Year Period',
    'Peak Season',
    'Weekends',
    'Public Holidays',
    'Special Events',
  ];

  const nonInclusiveOptions = [
    'Dinner',
    'Lunch',
    'Breakfast',
    'Alcohol',
    'Room Service',
    'Spa Services',
    'Airport Transfer',
    'Laundry Service',
  ];

  // Create Contract Modal Steps
  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6">
            <div className="flex items-center space-x-3">
              <FaBuilding className="text-black text-xl" />
              <h3 className="text-lg font-semibold text-black">Basic Contract Information</h3>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <label className="block text-sm font-medium text-black mb-1">Company *</label>
                <select
                  name="companyId"
                  value={newContract.companyId}
                  onChange={handleInputChange}
                  className="w-full p-3 border border-gray-300 rounded-lg bg-white text-black"
                  required
                >
                  <option value="">Select Company</option>
                  {companies.map((company) => (
                    <option key={company.companyid} value={company.companyid}>
                      {company.companyname}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-black mb-1">Contract ID</label>
                <input
                  type="text"
                  name="contractId"
                  value={newContract.contractId}
                  onChange={handleInputChange}
                  className="w-full p-3 border border-gray-300 rounded-lg bg-white text-black"
                  placeholder="e.g., CT-2025-001"
                />
                <p className="text-xs text-gray-500 mt-1">Optional: Enter a custom contract ID</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-black mb-1">Contract Name *</label>
                <input
                  type="text"
                  name="contractname"
                  value={newContract.contractname}
                  onChange={handleInputChange}
                  className="w-full p-3 border border-gray-300 rounded-lg bg-white text-black"
                  placeholder="e.g., Corporate Contract 2025"
                  required
                />
              </div>

              <div className="col-span-2">
                <label className="block text-sm font-medium text-black mb-1">
                  Contract Description
                </label>
                <textarea
                  name="contractDescription"
                  value={newContract.contractDescription}
                  onChange={handleInputChange}
                  rows="3"
                  className="w-full p-3 border border-gray-300 rounded-lg bg-white text-black"
                  placeholder="Describe the purpose and overview of this contract..."
                />
              </div>

              <div className="col-span-2">
                <label className="block text-sm font-medium text-black mb-1">GST Number</label>
                <input
                  type="text"
                  name="gst_number"
                  value={newContract.gst_number}
                  onChange={handleInputChange}
                  className="w-full p-3 border border-gray-300 rounded-lg bg-white text-black"
                  placeholder="27AAAAA0000A1Z5"
                />
              </div>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <div className="flex items-center space-x-3">
              <FaCalendarAlt className="text-black text-xl" />
              <h3 className="text-lg font-semibold text-black">Contract Duration</h3>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-black mb-1">Start Date *</label>
                <input
                  type="date"
                  name="contractStartDate"
                  value={newContract.contractStartDate}
                  onChange={handleInputChange}
                  className="w-full p-3 border border-gray-300 rounded-lg bg-white text-black"
                  required
                  min={new Date().toISOString().split('T')[0]} // Today's date as minimum
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-black mb-1">End Date *</label>
                <input
                  type="date"
                  name="contractEndDate"
                  value={newContract.contractEndDate}
                  onChange={handleInputChange}
                  className="w-full p-3 border border-gray-300 rounded-lg bg-white text-black"
                  required
                  min={newContract.contractStartDate || new Date().toISOString().split('T')[0]}
                />
              </div>

              <div className="col-span-2">
                <Toggle
                  isOn={newContract.autoRenewal}
                  onToggle={(value) => handleToggleChange('autoRenewal', value)}
                  title="Auto Renewal"
                  description="Contract will automatically renew upon expiration"
                  name="autoRenewal"
                />
              </div>

              {newContract.autoRenewal && (
                <div>
                  <label className="block text-sm font-medium text-black mb-1">
                    Renewal Notice Period (Days)
                  </label>
                  <input
                    type="number"
                    name="renewalNoticePeriod"
                    value={newContract.renewalNoticePeriod}
                    onChange={handleInputChange}
                    className="w-full p-3 border border-gray-300 rounded-lg bg-white text-black"
                    min="1"
                  />
                </div>
              )}
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <div className="flex items-center space-x-3">
              <FaFileContract className="text-black text-xl" />
              <h3 className="text-lg font-semibold text-black">Terms & Conditions</h3>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-black mb-1">
                  Terms and Conditions *
                </label>
                <textarea
                  name="termsAndConditions"
                  value={newContract.termsAndConditions}
                  onChange={handleInputChange}
                  rows="6"
                  className="w-full p-3 border border-gray-300 rounded-lg bg-white text-black"
                  placeholder="Enter the terms and conditions for this contract..."
                  required
                />
              </div>
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-6">
            <div className="flex items-center space-x-3">
              <FaMoneyBillWave className="text-black text-xl" />
              <h3 className="text-lg font-semibold text-black">Payment Terms</h3>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-black mb-1">Billing Cycle</label>
                <select
                  name="paymentTerms.billingCycle"
                  value={newContract.paymentTerms.billingCycle}
                  onChange={handleInputChange}
                  className="w-full p-3 border border-gray-300 rounded-lg bg-white text-black"
                >
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                  <option value="quarterly">Quarterly</option>
                  <option value="annually">Annually</option>
                </select>
              </div>

              {/* <div>
                <label className="block text-sm font-medium text-black mb-1">Base Price *</label>
                <input
                  type="number"
                  name="paymentTerms.baseprice"
                  value={newContract.paymentTerms.baseprice}
                  onChange={handleInputChange}
                  className="w-full p-3 border border-gray-300 rounded-lg bg-white text-black"
                  placeholder="0.00"
                  required
                  min="0"
                  step="0.01"
                />
              </div> */}

              <div>
                <label className="block text-sm font-medium text-black mb-1">Credit Limit</label>
                <input
                  type="number"
                  name="paymentTerms.creditlimit"
                  value={newContract.paymentTerms.creditlimit}
                  onChange={handleInputChange}
                  className="w-full p-3 border border-gray-300 rounded-lg bg-white text-black"
                  placeholder="0.00"
                  min="0"
                  step="0.01"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-black mb-1">
                  Late Payment Fee
                </label>
                <input
                  type="number"
                  name="paymentTerms.latePaymentFee"
                  value={newContract.paymentTerms.latePaymentFee}
                  onChange={handleInputChange}
                  className="w-full p-3 border border-gray-300 rounded-lg bg-white text-black"
                  placeholder="0.00"
                  min="0"
                  step="0.01"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-black mb-1">
                  Payment Due Days
                </label>
                <input
                  type="number"
                  name="paymentTerms.paymentDueDays"
                  value={newContract.paymentTerms.paymentDueDays}
                  onChange={handleInputChange}
                  className="w-full p-3 border border-gray-300 rounded-lg bg-white text-black"
                  placeholder="30"
                  min="1"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-black mb-1">Invoicing Date</label>
                <input
                  type="text"
                  name="paymentTerms.invoicingDate"
                  value={newContract.paymentTerms.invoicingDate}
                  onChange={handleInputChange}
                  className="w-full p-3 border border-gray-300 rounded-lg bg-white text-black"
                  placeholder="e.g., 1 (for 1st of month) or 2025-01-05"
                />
              </div>
            </div>
          </div>
        );

      case 5:
        return (
          <div className="space-y-6">
            <div className="flex items-center space-x-3">
              <FaBan className="text-black text-xl" />
              <h3 className="text-lg font-semibold text-black">Withdrawal & Termination</h3>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-black mb-1">
                  Withdrawal Notice Period (Days)
                </label>
                <input
                  type="number"
                  name="withdrawalTerms.withdrawalNotice"
                  value={newContract.withdrawalTerms.withdrawalNotice}
                  onChange={handleInputChange}
                  className="w-full p-3 border border-gray-300 rounded-lg bg-white text-black"
                  min="1"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-black mb-1">
                  Withdrawal Penalty
                </label>
                <input
                  type="text"
                  name="withdrawalTerms.withdrawalPenalty"
                  value={newContract.withdrawalTerms.withdrawalPenalty}
                  onChange={handleInputChange}
                  className="w-full p-3 border border-gray-300 rounded-lg bg-white text-black"
                  placeholder="e.g., 50% of remaining contract value"
                />
              </div>

              <div className="col-span-2">
                <label className="block text-sm font-medium text-black mb-1">
                  Force Majeure Clause
                </label>
                <textarea
                  name="withdrawalTerms.forceMajeure"
                  value={newContract.withdrawalTerms.forceMajeure}
                  onChange={handleInputChange}
                  rows="3"
                  className="w-full p-3 border border-gray-300 rounded-lg bg-white text-black"
                  placeholder="Terms for unforeseen circumstances..."
                />
              </div>

              <div className="col-span-2">
                <Toggle
                  isOn={newContract.withdrawalTerms.penaltyApplicable}
                  onToggle={(value) =>
                    handleToggleChange('withdrawalTerms.penaltyApplicable', value)
                  }
                  title="Penalty Applicable"
                  description="Enable if withdrawal penalties apply"
                  name="withdrawalTerms.penaltyApplicable"
                />
              </div>
            </div>
          </div>
        );
      case 6:
        return (
          <div className="space-y-6">
            <div className="flex items-center space-x-3">
              <FaPercentage className="text-black text-xl" />
              <h3 className="text-lg font-semibold text-black">Discount Structure</h3>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-black mb-3">Discount Type</label>
                <div className="grid grid-cols-2 gap-4">
                  <label className="flex items-center space-x-3 p-3 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50">
                    <input
                      type="radio"
                      name="discountStructure.discountType"
                      value="flat"
                      checked={newContract.discountStructure.discountType === 'flat'}
                      onChange={handleInputChange}
                      className="text-black"
                    />
                    <span className="text-sm font-medium text-black">Flat Discount (₹)</span>
                  </label>

                  <label className="flex items-center space-x-3 p-3 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50">
                    <input
                      type="radio"
                      name="discountStructure.discountType"
                      value="slab"
                      checked={newContract.discountStructure.discountType === 'slab'}
                      onChange={handleInputChange}
                      className="text-black"
                    />
                    <span className="text-sm font-medium text-black">Slab-wise Discount (%)</span>
                  </label>
                </div>
              </div>

              {newContract.discountStructure.discountType === 'flat' && (
                <div>
                  <label className="block text-sm font-medium text-black mb-1">
                    Flat Discount Amount (₹)
                  </label>
                  <input
                    type="number"
                    name="discountStructure.flatDiscount"
                    value={newContract.discountStructure.flatDiscount}
                    onChange={handleInputChange}
                    min="0"
                    step="0.01"
                    className="w-full p-3 border border-gray-300 rounded-lg bg-white text-black"
                    placeholder="Enter discount amount in rupees"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Enter the fixed discount amount in Indian Rupees (₹)
                  </p>
                </div>
              )}

              {newContract.discountStructure.discountType === 'slab' && (
                <div className="space-y-3">
                  <label className="block text-sm font-medium text-black">Discount Slabs (%)</label>
                  {newContract.discountStructure.discountSlabs.map((slab, index) => (
                    <div key={index} className="flex items-center space-x-3">
                      <input
                        type="number"
                        value={slab.minAmount}
                        onChange={(e) =>
                          handleDiscountSlabChange(
                            index,
                            'minAmount',
                            parseFloat(e.target.value) || 0
                          )
                        }
                        className="flex-1 p-3 border border-gray-300 rounded-lg bg-white text-black"
                        placeholder="Minimum amount (₹)"
                        min="0"
                        step="0.01"
                      />
                      <input
                        type="number"
                        value={slab.discountPercent}
                        onChange={(e) =>
                          handleDiscountSlabChange(
                            index,
                            'discountPercent',
                            parseFloat(e.target.value) || 0
                          )
                        }
                        className="flex-1 p-3 border border-gray-300 rounded-lg bg-white text-black"
                        placeholder="Discount %"
                        min="0"
                        max="100"
                        step="0.01"
                      />
                      {newContract.discountStructure.discountSlabs.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeDiscountSlab(index)}
                          className="p-3 text-red-600 hover:bg-red-50 rounded-lg transition duration-200"
                        >
                          <FaTimesCircle />
                        </button>
                      )}
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={addDiscountSlab}
                    className="flex items-center space-x-2 text-black hover:text-black font-medium"
                  >
                    <FaPlus className="text-sm" />
                    <span>Add Another Slab</span>
                  </button>
                  <p className="text-xs text-gray-500">
                    Slab 1: For bookings above ₹
                    {newContract.discountStructure.discountSlabs[0]?.minAmount || 0}, apply{' '}
                    {newContract.discountStructure.discountSlabs[0]?.discountPercent || 0}% discount
                  </p>
                </div>
              )}
              <Toggle
                isOn={newContract.discountStructure.inclusiveDiscount}
                onToggle={(value) =>
                  handleToggleChange('discountStructure.inclusiveDiscount', value)
                }
                title="Inclusive of all taxes and charges"
                description="Discount includes all applicable taxes and additional charges"
                name="discountStructure.inclusiveDiscount"
              />

              <div className="space-y-3">
                <label className="block text-sm font-medium text-black">
                  Non-Applicable Conditions
                </label>
                <div className="grid grid-cols-3 gap-3">
                  {nonApplicableOptions.map((condition) => (
                    <div
                      key={condition}
                      className="flex items-center justify-between p-3 border border-gray-200 rounded-lg"
                    >
                      <span className="text-sm text-black">{condition}</span>
                      <Toggle
                        isOn={newContract.discountStructure.nonApplicableConditions.includes(
                          condition
                        )}
                        onToggle={(checked) => handleNonApplicableChange(condition, checked)}
                        title=""
                        description=""
                        name={`nonApplicable-${condition}`}
                      />
                    </div>
                  ))}
                </div>
              </div>

              <div className="space-y-3">
                <label className="block text-sm font-medium text-black">Non-Inclusive Items</label>
                <div className="grid grid-cols-3 gap-3">
                  {nonInclusiveOptions.map((item) => (
                    <div
                      key={item}
                      className="flex items-center justify-between p-3 border border-gray-200 rounded-lg"
                    >
                      <span className="text-sm text-black">{item}</span>
                      <Toggle
                        isOn={newContract.discountStructure.nonInclusiveItems.includes(item)}
                        onToggle={(checked) => handleNonInclusiveChange(item, checked)}
                        title=""
                        description=""
                        name={`nonInclusive-${item}`}
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        );

      case 7:
        return (
          <div className="space-y-6">
            <div className="flex items-center space-x-3">
              <FaUserCheck className="text-black text-xl" />
              <h3 className="text-lg font-semibold text-black">Contact & Signatory Details</h3>
            </div>

            <div className="grid grid-cols-2 gap-6">
              <div className="space-y-4">
                <h4 className="text-md font-semibold text-black border-b pb-2">Contact Details</h4>
                <div>
                  <label className="block text-sm font-medium text-black mb-1">
                    Contact Person *
                  </label>
                  <input
                    type="text"
                    name="contactDetails.contactPerson"
                    value={newContract.contactDetails.contactPerson}
                    onChange={handleInputChange}
                    className="w-full p-3 border border-gray-300 rounded-lg bg-white text-black"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-black mb-1">
                    Contact Email *
                  </label>
                  <input
                    type="email"
                    name="contactDetails.contactEmail"
                    value={newContract.contactDetails.contactEmail}
                    onChange={handleInputChange}
                    className="w-full p-3 border border-gray-300 rounded-lg bg-white text-black"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-black mb-1">Contact Phone</label>
                  <input
                    type="tel"
                    name="contactDetails.contactPhone"
                    value={newContract.contactDetails.contactPhone}
                    onChange={handleInputChange}
                    className="w-full p-3 border border-gray-300 rounded-lg bg-white text-black"
                  />
                </div>
              </div>

              <div className="space-y-4">
                <h4 className="text-md font-semibold text-black border-b pb-2">
                  Signatory Details
                </h4>
                <div>
                  <label className="block text-sm font-medium text-black mb-1">
                    Signatory Name *
                  </label>
                  <input
                    type="text"
                    name="signatoryDetails.signatoryName"
                    value={newContract.signatoryDetails.signatoryName}
                    onChange={handleInputChange}
                    className="w-full p-3 border border-gray-300 rounded-lg bg-white text-black"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-black mb-1">
                    Signatory Title *
                  </label>
                  <input
                    type="text"
                    name="signatoryDetails.signatoryTitle"
                    value={newContract.signatoryDetails.signatoryTitle}
                    onChange={handleInputChange}
                    className="w-full p-3 border border-gray-300 rounded-lg bg-white text-black"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-black mb-1">
                    Signatory Email *
                  </label>
                  <input
                    type="email"
                    name="signatoryDetails.signatoryEmail"
                    value={newContract.signatoryDetails.signatoryEmail}
                    onChange={handleInputChange}
                    className="w-full p-3 border border-gray-300 rounded-lg bg-white text-black "
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-black mb-1">
                    Signatory Phone
                  </label>
                  <input
                    type="tel"
                    name="signatoryDetails.signatoryPhone"
                    value={newContract.signatoryDetails.signatoryPhone}
                    onChange={handleInputChange}
                    className="w-full p-3 border border-gray-300 rounded-lg bg-white text-black "
                  />
                </div>
              </div>
            </div>
          </div>
        );

      case 8:
        return (
          <div className="space-y-6">
            <div className="flex items-center space-x-3">
              <FaCheckCircle className="text-black text-xl" />
              <h3 className="text-lg font-semibold text-black">Review & Submit</h3>
            </div>

            <div className="bg-gray-50 rounded-lg p-6 space-y-6">
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <h4 className="text-sm font-semibold text-black mb-2">Company Details</h4>
                  <p className="text-sm text-black">{getCompanyName(newContract.companyId)}</p>
                  {newContract.contractId && (
                    <p className="text-sm text-black">Contract ID: {newContract.contractId}</p>
                  )}
                  <p className="text-sm text-black">{newContract.contactDetails.contactPerson}</p>
                  <p className="text-sm text-black">{newContract.contactDetails.contactEmail}</p>
                </div>

                <div>
                  <h4 className="text-sm font-semibold text-black mb-2">Contract Duration</h4>
                  <p className="text-sm text-black">
                    {formatDate(newContract.contractStartDate)} -{' '}
                    {formatDate(newContract.contractEndDate)}
                  </p>
                  {newContract.autoRenewal && (
                    <p className="text-sm text-black">
                      Auto-renewal enabled ({newContract.renewalNoticePeriod} days notice)
                    </p>
                  )}
                </div>

                <div>
                  <h4 className="text-sm font-semibold text-black mb-2">Pricing</h4>
                  {/* <p className="text-sm text-black">Base Price: {newContract.paymentTerms.currency} {newContract.paymentTerms.baseprice}</p> */}
                  <p className="text-sm text-black">
                    Credit Limit: {newContract.paymentTerms.currency}{' '}
                    {newContract.paymentTerms.creditlimit}
                  </p>
                  {newContract.discountStructure.discountType === 'flat' &&
                    newContract.discountStructure.flatDiscount > 0 && (
                      <p className="text-sm text-black">
                        Flat Discount: ₹{newContract.discountStructure.flatDiscount}
                      </p>
                    )}
                  {newContract.discountStructure.discountType === 'slab' &&
                    newContract.discountStructure.discountSlabs.length > 0 && (
                      <p className="text-sm text-black">
                        Slab Discount: Starts at{' '}
                        {newContract.discountStructure.discountSlabs[0].discountPercent}%
                      </p>
                    )}
                </div>

                <div>
                  <h4 className="text-sm font-semibold text-black mb-2">Billing</h4>
                  <p className="text-sm text-black capitalize">
                    {newContract.paymentTerms.billingCycle} billing
                  </p>
                  <p className="text-sm text-black">
                    Payment: Net {newContract.paymentTerms.paymentDueDays} days
                  </p>
                </div>

                <div className="col-span-2">
                  <h4 className="text-sm font-semibold text-black mb-2">Signatory</h4>
                  <p className="text-sm text-black">{newContract.signatoryDetails.signatoryName}</p>
                  <p className="text-sm text-black">
                    {newContract.signatoryDetails.signatoryTitle}
                  </p>
                </div>
              </div>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  // Filter contracts based on active tab and other filters
  const filteredContracts = contracts.filter((contract) => {
    const now = new Date();
    const startDateObj = new Date(contract.contractStartDate);
    const endDateObj = new Date(contract.contractEndDate);

    let contractStatus = 'draft';
    if (now >= startDateObj && now <= endDateObj) {
      contractStatus = 'active';
    } else if (now > endDateObj) {
      contractStatus = 'expired';
    }

    const matchesSearch =
      contract.contractname?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      contract.companyid?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus = statusFilter === 'all' || contractStatus === statusFilter;

    let matchesDate = true;
    if (startDate && contract.contractStartDate < startDate) matchesDate = false;
    if (endDate && contract.contractEndDate > endDate) matchesDate = false;

    return matchesSearch && matchesStatus && matchesDate;
  });

  // Pagination logic
  const totalPages = Math.ceil(filteredContracts.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentContracts = filteredContracts.slice(startIndex, startIndex + itemsPerPage);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getCompanyName = (companyId) => {
    const company = companies.find((c) => c.companyid === companyId);
    return company ? company.companyname : companyId;
  };

  const getContractStatusBadge = (contract) => {
    // Use the status from API response
    const status = contract.status?.toLowerCase() || 'pending';
    const rejectionReason = contract.rejection_reason;

    const statusConfig = {
      approved: {
        color: 'bg-green-100 text-green-800',
        label: 'Approved',
        icon: FaThumbsUp,
        description: 'Contract has been approved',
      },
      rejected: {
        color: 'bg-red-100 text-red-800',
        label: 'Rejected',
        icon: FaThumbsDown,
        description: 'Contract has been rejected',
      },
      pending: {
        color: 'bg-yellow-100 text-yellow-800',
        label: 'Pending',
        icon: FaClock,
        description: 'Awaiting approval',
      },
      draft: {
        color: 'bg-gray-100 text-gray-800',
        label: 'Draft',
        icon: FaFileContract,
        description: 'Draft contract',
      },
    };

    const config = statusConfig[status] || {
      color: 'bg-gray-100 text-gray-800',
      label: status.charAt(0).toUpperCase() + status.slice(1),
      icon: FaClock,
      description: 'Contract status',
    };

    const IconComponent = config.icon;

    return (
      <div className="space-y-1">
        <span
          className={`px-3 py-1 rounded-full text-xs font-medium ${config.color} flex items-center space-x-1`}
        >
          <IconComponent className="text-xs" />
          <span>{config.label}</span>
        </span>

        {/* Show rejection reason if contract is rejected */}
        {status === 'rejected' && rejectionReason && (
          <div className="text-xs text-gray-600 mt-1 max-w-xs">
            <div className="font-medium text-red-700 mb-1">Rejection Reason:</div>
            <div className="truncate" title={rejectionReason}>
              {rejectionReason.length > 30
                ? `${rejectionReason.substring(0, 30)}...`
                : rejectionReason}
            </div>
          </div>
        )}

        {/* Show approval date if approved */}
        {status === 'approved' && contract.approved_at && (
          <div className="text-xs text-gray-600 mt-1">
            <div className="font-medium text-green-700">Approved on:</div>
            <div>{formatDate(contract.approved_at)}</div>
          </div>
        )}

        {/* Show pending message */}
        {status === 'pending' && <div className="text-xs text-gray-500 mt-1">Awaiting review</div>}
      </div>
    );
  };

  const getStatusBadge = (contract) => {
    const now = new Date();
    const startDate = new Date(contract.contractStartDate);
    const endDate = new Date(contract.contractEndDate);

    let status = 'draft';
    if (now >= startDate && now <= endDate) {
      status = 'active';
    } else if (now > endDate) {
      status = 'expired';
    }

    const statusConfig = {
      active: {
        color: 'bg-green-100 text-green-800',
        label: 'Active',
        icon: FaCheckCircle,
      },
      draft: {
        color: 'bg-yellow-100 text-yellow-800',
        label: 'Draft',
        icon: FaClock,
      },
      expired: {
        color: 'bg-red-100 text-red-800',
        label: 'Expired',
        icon: FaTimesCircle,
      },
    };

    const config = statusConfig[status] || {
      color: 'bg-gray-100 text-gray-800',
      label: status,
      icon: FaClock,
    };
    const IconComponent = config.icon;

    return (
      <span
        className={`px-3 py-1 rounded-full text-xs font-medium ${config.color} flex items-center space-x-1`}
      >
        <IconComponent className="text-xs" />
        <span>{config.label}</span>
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <ToastContainer />

      {/* Header Section */}
      <header className="flex justify-between items-center mb-3 bg-white p-3">
        <h1 className="text-black text-xl font-bold ml-2">Customer Management</h1>
        <div className="flex space-x-6">
          <button
            onClick={() => navigate('/dashboard/mdm/customer-management/corporate-overview')}
            className="btn"
          >
            Overview
          </button>
          <button
            onClick={() => navigate('/dashboard/mdm/customer-management/leisure')}
            className="btn"
          >
            Guest Management
          </button>
          <button
            onClick={() => navigate('/dashboard/mdm/customer-management/corporate')}
            className="btn"
          >
            Corporate Accounts
          </button>
          <button
            onClick={() => navigate('/dashboard/mdm/customer-management/contracts')}
            className="btn text-black font-bold"
          >
            Contracts
          </button>
          <button
            onClick={() => navigate('/dashboard/mdm/customer-management/credit-verification')}
            className="btn"
          >
            Credit & Verification
          </button>
          <button
            onClick={() => navigate('/dashboard/mdm/customer-management/settings')}
            className="btn "
          >
            Settings
          </button>
        </div>
      </header>

      {/* Main Content */}
      <main className="bg-white rounded-lg shadow-sm">
        {/* Filter Bar */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between">
            <div className="flex flex-col sm:flex-row gap-4 flex-1 w-full">
              {/* Search Input */}
              <div className="relative flex-1 max-w-md">
                <FaSearch className="absolute left-3 top-3 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search contracts..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg bg-white text-black focus:ring-2 focus:ring-black focus:border-black"
                />
              </div>

              {/* Filters */}
              <div className="flex gap-3">
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="px-3 py-2.5 border border-gray-300 rounded-lg bg-white text-black focus:ring-2 focus:ring-black focus:border-black"
                >
                  <option value="all">All Status</option>
                  <option value="active">Active</option>
                  <option value="draft">Draft</option>
                  <option value="expired">Expired</option>
                </select>

                <div className="flex gap-2">
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="px-3 py-2.5 border border-gray-300 rounded-lg bg-white text-black focus:ring-2 focus:ring-black focus:border-black"
                    placeholder="Start Date"
                  />
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="px-3 py-2.5 border border-gray-300 rounded-lg bg-white text-black focus:ring-2 focus:ring-black focus:border-black"
                    placeholder="End Date"
                  />
                </div>
              </div>
            </div>

            {/* Create New Contract Button */}
            <div className="flex space-x-4">
              <button
                onClick={() => navigate('/dashboard/mdm/customer-management/contract-approvals')}
                className="bg-black text-white px-6 py-2.5 rounded-lg hover:bg-black transition duration-200 font-semibold flex items-center space-x-2"
              >
                <FaCheckCircle className="text-sm" />
                <span>Contract Approvals</span>
              </button>

              <button
                onClick={() => setShowCreateModal(true)}
                className="bg-black text-white px-6 py-2.5 rounded-lg hover:bg-gray-800 transition duration-200 font-semibold flex items-center space-x-2"
              >
                <FaPlus className="text-sm" />
                <span>Create Contract</span>
              </button>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-semibold text-black uppercase tracking-wider border-b border-gray-200">
                  Contract Details
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-black uppercase tracking-wider border-b border-gray-200">
                  Duration
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-black uppercase tracking-wider border-b border-gray-200">
                  Pricing
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-black uppercase tracking-wider border-b border-gray-200">
                  Contract Status
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-black uppercase tracking-wider border-b border-gray-200">
                  Status
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-black uppercase tracking-wider border-b border-gray-200">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="5" className="px-6 py-8 text-center">
                    <div className="flex justify-center items-center">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-black"></div>
                      <span className="ml-2 text-black">Loading contracts...</span>
                    </div>
                  </td>
                </tr>
              ) : currentContracts.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-6 py-8 text-center text-black">
                    <FaFileContract className="mx-auto text-4xl text-gray-300 mb-3" />
                    <p>No contracts found</p>
                    <button
                      onClick={() => setShowCreateModal(true)}
                      className="mt-2 text-black hover:text-black font-medium"
                    >
                      Create your first contract
                    </button>
                  </td>
                </tr>
              ) : (
                currentContracts.map((contract) => (
                  <tr
                    key={contract.contractid}
                    className="hover:bg-gray-50 transition duration-150"
                  >
                    <td className="px-6 py-4">
                      <div>
                        <div className="font-semibold text-black">{contract.contractname}</div>
                        <div className="text-sm text-black mt-1">
                          {getCompanyName(contract.companyid)}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-black whitespace-nowrap">
                        <div>
                          {formatDate(contract.contractStartDate)} to{' '}
                          {formatDate(contract.contractEndDate)}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-black">
                        {/* Display discount information based on contract type */}
                        {contract.contract_discount_structure?.discount_type === 'flat' &&
                        contract.contract_discount_structure?.flat_discount &&
                        parseFloat(contract.contract_discount_structure.flat_discount) > 0 ? (
                          <div className="font-medium text-green-600">
                            Flat Discount: ₹
                            {parseFloat(
                              contract.contract_discount_structure.flat_discount
                            ).toLocaleString()}
                          </div>
                        ) : contract.contract_discount_slab &&
                          contract.contract_discount_slab.length > 0 &&
                          contract.contract_discount_slab.some(
                            (slab) => parseFloat(slab.discount_percent) > 0
                          ) ? (
                          <div className="space-y-1">
                            {contract.contract_discount_slab
                              .filter((slab) => parseFloat(slab.discount_percent) > 0)
                              .sort((a, b) => parseFloat(a.min_amount) - parseFloat(b.min_amount))
                              .map((slab, index) => (
                                <div key={index} className="font-medium text-blue-600">
                                  {parseFloat(slab.min_amount) > 0 ? (
                                    <>
                                      Above ₹{parseFloat(slab.min_amount).toLocaleString()}:{' '}
                                      {parseFloat(slab.discount_percent)}% off
                                    </>
                                  ) : (
                                    <>Base: {parseFloat(slab.discount_percent)}% off</>
                                  )}
                                </div>
                              ))}
                          </div>
                        ) : (
                          <div className="text-gray-500">No discount</div>
                        )}

                        {/* Display other pricing details */}
                        <div className="text-gray-600 capitalize mt-1">
                          {contract.contract_discount_structure?.rate_type || 'Standard Rate'}
                        </div>
                        {contract.contract_discount_structure?.base_price > 0 && (
                          <div className="text-gray-600">
                            Base: ₹
                            {parseFloat(
                              contract.contract_discount_structure.base_price
                            ).toLocaleString()}
                          </div>
                        )}
                        {contract.contract_discount_structure?.credit_limit > 0 && (
                          <div className="text-gray-600">
                            Credit: ₹
                            {parseFloat(
                              contract.contract_discount_structure.credit_limit
                            ).toLocaleString()}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">{getContractStatusBadge(contract)}</td>
                    <td className="px-6 py-4">{getStatusBadge(contract)}</td>
                    <td className="px-6 py-4">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleViewContract(contract)}
                          className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition duration-150"
                          title="View contract details"
                        >
                          <FaEye size={16} />
                        </button>
                        <button
                          onClick={() => handleDownloadContract(contract)}
                          className="p-2 text-gray-600 hover:text-green-600 hover:bg-green-50 rounded-lg transition duration-150"
                          title="Download contract PDF"
                        >
                          <FaDownload size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-6 py-4 bg-white border-t border-gray-200">
            <div className="flex items-center justify-between">
              <div className="text-sm text-black">
                Showing {startIndex + 1} to{' '}
                {Math.min(startIndex + itemsPerPage, filteredContracts.length)} of{' '}
                {filteredContracts.length} contracts
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition duration-200 font-semibold disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                  <button
                    key={page}
                    onClick={() => handlePageChange(page)}
                    className={`px-4 py-2 rounded-lg font-semibold transition duration-200 ${
                      currentPage === page
                        ? 'bg-black text-white'
                        : 'bg-gray-200 text-black hover:bg-gray-300'
                    }`}
                  >
                    {page}
                  </button>
                ))}
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition duration-200 font-semibold disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Create Contract Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white p-8 rounded-xl shadow-2xl w-full max-w-6xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center space-x-3">
                <FaFileContract className="text-2xl text-black" />
                <h2 className="text-2xl font-semibold text-black">Create Corporate Contract</h2>
              </div>
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  setCurrentStep(1);
                  resetNewContract();
                }}
                className="p-2 hover:bg-gray-100 rounded-lg transition duration-150"
              >
                <FaTimesCircle className="text-gray-400 hover:text-gray-600" />
              </button>
            </div>

            {/* Enhanced Step Indicator */}
            <StepIndicator currentStep={currentStep} steps={steps} />

            <form
              onSubmit={
                currentStep === 8
                  ? handleCreateContract
                  : (e) => {
                      e.preventDefault();
                      nextStep();
                    }
              }
            >
              {renderStep()}

              <div className="flex justify-between pt-6 mt-6 border-t">
                <button
                  type="button"
                  onClick={
                    currentStep === 1
                      ? () => {
                          setShowCreateModal(false);
                          resetNewContract();
                        }
                      : prevStep
                  }
                  className="flex items-center space-x-2 px-6 py-3 border border-gray-300 text-black rounded-lg hover:bg-gray-50 transition duration-200 font-semibold"
                >
                  <FaChevronLeft className="text-sm" />
                  <span>{currentStep === 1 ? 'Cancel' : 'Back'}</span>
                </button>
                <div className="flex space-x-4">
                  {currentStep < 8 && (
                    <button
                      type="submit"
                      className="flex items-center space-x-2 px-6 py-3 bg-black text-white rounded-lg hover:bg-gray-800 transition duration-200 font-semibold"
                    >
                      <span>Next</span>
                      <FaChevronRight className="text-sm" />
                    </button>
                  )}
                  {currentStep === 8 && (
                    <button
                      type="submit"
                      disabled={creatingContract}
                      className="flex items-center space-x-2 px-6 py-3 bg-black text-white rounded-lg hover:bg-gray-800 transition duration-200 font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {creatingContract ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                          <span>Creating...</span>
                        </>
                      ) : (
                        <>
                          <FaCheckCircle className="text-sm" />
                          <span>Create Contract</span>
                        </>
                      )}
                    </button>
                  )}
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Contract Modal */}
      {showViewModal && selectedContract && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-sm z-50">
          <div className="bg-white p-8 rounded-2xl shadow-2xl w-full max-w-6xl mx-4 max-h-[90vh] overflow-y-auto border border-gray-200">
            {/* Header */}
            <div className="flex items-center justify-between mb-8 pb-6 border-b border-gray-100">
              <div className="flex items-center space-x-4">
                <div className="p-3 bg-gradient-to-r from-gray-900 to-black rounded-2xl">
                  <FaFileContract className="text-2xl text-white" />
                </div>
                <div>
                  <h2 className="text-3xl font-bold text-gray-900">Contract Details</h2>
                  <p className="text-gray-600 mt-1">Complete contract information and status</p>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                {selectedContract.hasPdf && (
                  <button
                    onClick={() => handleDownloadContract(selectedContract)}
                    className="flex items-center space-x-2 px-4 py-2 bg-gray-900 text-white rounded-xl hover:bg-black transition-all duration-200 font-semibold shadow-lg hover:shadow-xl"
                  >
                    <FaDownload size={16} />
                    <span>Download PDF</span>
                  </button>
                )}
                {getStatusBadge(selectedContract)}
              </div>
            </div>

            {/* Main Content Grid */}
            <div className="space-y-8">
              {/* Contract Overview */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div className="space-y-6">
                  <div className="bg-gradient-to-br from-gray-50 to-white p-6 rounded-2xl border border-gray-100 shadow-sm">
                    <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center space-x-2">
                      <div className="w-2 h-2 bg-gray-900 rounded-full"></div>
                      <span>Basic Information</span>
                    </h3>
                    <div className="space-y-4">
                      <div className="flex justify-between items-center py-2 border-b border-gray-100">
                        <span className="text-sm font-medium text-gray-600">Contract Name</span>
                        <span className="text-sm font-semibold text-gray-900 text-right">
                          {selectedContract.contractname}
                        </span>
                      </div>
                      <div className="flex justify-between items-center py-2 border-b border-gray-100">
                        <span className="text-sm font-medium text-gray-600">Company</span>
                        <span className="text-sm font-semibold text-gray-900 text-right">
                          {selectedContract.company?.companyname || selectedContract.companyid}
                        </span>
                      </div>
                      <div className="flex justify-between items-center py-2 border-b border-gray-100">
                        <span className="text-sm font-medium text-gray-600">Contract ID</span>
                        <span className="text-sm font-mono text-gray-900 bg-gray-50 px-2 py-1 rounded">
                          {selectedContract.contractid}
                        </span>
                      </div>
                      {selectedContract.contractId && (
                        <div className="flex justify-between items-center py-2 border-b border-gray-100">
                          <span className="text-sm font-medium text-gray-600">
                            Custom Contract ID
                          </span>
                          <span className="text-sm font-semibold text-gray-900 text-right">
                            {selectedContract.contractId}
                          </span>
                        </div>
                      )}
                      <div className="flex justify-between items-center py-2">
                        <span className="text-sm font-medium text-gray-600">GST Number</span>
                        <span className="text-sm font-semibold text-gray-900">
                          {selectedContract.gst_number || 'N/A'}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Contact Information */}
                  <div className="bg-gradient-to-br from-gray-50 to-white p-6 rounded-2xl border border-gray-100 shadow-sm">
                    <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center space-x-2">
                      <div className="w-2 h-2 bg-gray-900 rounded-full"></div>
                      <span>Contact Information</span>
                    </h3>
                    <div className="space-y-4">
                      <div className="flex justify-between items-center py-2 border-b border-gray-100">
                        <span className="text-sm font-medium text-gray-600">Contact Person</span>
                        <span className="text-sm font-semibold text-gray-900">
                          {selectedContract.contactDetails.contactPerson}
                        </span>
                      </div>
                      <div className="flex justify-between items-center py-2 border-b border-gray-100">
                        <span className="text-sm font-medium text-gray-600">Email</span>
                        <span className="text-sm text-gray-900">
                          {selectedContract.contactDetails.contactEmail}
                        </span>
                      </div>
                      <div className="flex justify-between items-center py-2">
                        <span className="text-sm font-medium text-gray-600">Phone</span>
                        <span className="text-sm text-gray-900">
                          {selectedContract.contactDetails.contactPhone || 'N/A'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="space-y-6">
                  <div className="bg-gradient-to-br from-gray-50 to-white p-6 rounded-2xl border border-gray-100 shadow-sm">
                    <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center space-x-2">
                      <div className="w-2 h-2 bg-gray-900 rounded-full"></div>
                      <span>Duration & Status</span>
                    </h3>
                    <div className="space-y-4">
                      <div className="py-2 border-b border-gray-100">
                        <span className="text-sm font-medium text-gray-600 block mb-1">
                          Contract Period
                        </span>
                        <span className="text-sm font-semibold text-gray-900">
                          {formatDate(selectedContract.contractStartDate)} to{' '}
                          {formatDate(selectedContract.contractEndDate)}
                        </span>
                        {selectedContract.isExpired && (
                          <div className="inline-flex items-center space-x-1 mt-2 px-3 py-1 bg-red-50 text-red-700 rounded-full text-xs font-medium">
                            <FaTimesCircle className="text-xs" />
                            <span>Contract Expired</span>
                          </div>
                        )}
                      </div>
                      <div className="flex justify-between items-center py-2 border-b border-gray-100">
                        <span className="text-sm font-medium text-gray-600">Auto Renewal</span>
                        <span
                          className={`text-sm font-semibold ${selectedContract.autoRenewal ? 'text-green-600' : 'text-gray-600'}`}
                        >
                          {selectedContract.autoRenewal ? 'Enabled' : 'Disabled'}
                        </span>
                      </div>
                      <div className="flex justify-between items-center py-2">
                        <span className="text-sm font-medium text-gray-600">Renewal Notice</span>
                        <span className="text-sm font-semibold text-gray-900">
                          {selectedContract.renewalNoticePeriod} days
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Signatory Details */}
                  <div className="bg-gradient-to-br from-gray-50 to-white p-6 rounded-2xl border border-gray-100 shadow-sm">
                    <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center space-x-2">
                      <div className="w-2 h-2 bg-gray-900 rounded-full"></div>
                      <span>Signatory Details</span>
                    </h3>
                    <div className="space-y-4">
                      <div className="flex justify-between items-center py-2 border-b border-gray-100">
                        <span className="text-sm font-medium text-gray-600">Signatory Name</span>
                        <span className="text-sm font-semibold text-gray-900">
                          {selectedContract.signatoryDetails.signatoryName}
                        </span>
                      </div>
                      <div className="flex justify-between items-center py-2 border-b border-gray-100">
                        <span className="text-sm font-medium text-gray-600">Title</span>
                        <span className="text-sm text-gray-900">
                          {selectedContract.signatoryDetails.signatoryTitle}
                        </span>
                      </div>
                      <div className="flex justify-between items-center py-2 border-b border-gray-100">
                        <span className="text-sm font-medium text-gray-600">Email</span>
                        <span className="text-sm text-gray-900">
                          {selectedContract.signatoryDetails.signatoryEmail}
                        </span>
                      </div>
                      <div className="flex justify-between items-center py-2">
                        <span className="text-sm font-medium text-gray-600">Phone</span>
                        <span className="text-sm text-gray-900">
                          {selectedContract.signatoryDetails.signatoryPhone || 'N/A'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {/* Contract Status Information */}
              <div className="bg-gradient-to-br from-gray-50 to-white p-6 rounded-2xl border border-gray-100 shadow-sm">
                <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center space-x-2">
                  <div className="w-2 h-2 bg-gray-900 rounded-full"></div>
                  <span>Contract Status</span>
                </h3>

                <div className="space-y-4">
                  {/* Current Status */}
                  <div className="flex items-center justify-between py-3 px-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center space-x-3">
                      {selectedContract.status === 'approved' && (
                        <FaThumbsUp className="text-green-600" />
                      )}
                      {selectedContract.status === 'rejected' && (
                        <FaThumbsDown className="text-red-600" />
                      )}
                      {selectedContract.status === 'pending' && (
                        <FaClock className="text-yellow-600" />
                      )}
                      {selectedContract.status === 'draft' && (
                        <FaFileContract className="text-gray-600" />
                      )}
                      <div>
                        <span className="text-sm font-medium text-gray-700">Current Status</span>
                        <div className="text-lg font-bold text-gray-900 capitalize">
                          {selectedContract.status || 'Unknown'}
                        </div>
                      </div>
                    </div>
                    <div
                      className={`px-4 py-2 rounded-full font-medium ${
                        selectedContract.status === 'approved'
                          ? 'bg-green-100 text-green-800'
                          : selectedContract.status === 'rejected'
                            ? 'bg-red-100 text-red-800'
                            : selectedContract.status === 'pending'
                              ? 'bg-yellow-100 text-yellow-800'
                              : 'bg-gray-100 text-gray-800'
                      }`}
                    >
                      {selectedContract.status === 'approved' && '✓ Approved'}
                      {selectedContract.status === 'rejected' && '✗ Rejected'}
                      {selectedContract.status === 'pending' && '⏳ Pending'}
                      {selectedContract.status === 'draft' && '📝 Draft'}
                    </div>
                  </div>

                  {/* Approved Details */}
                  {selectedContract.is_approved && selectedContract.approved_at && (
                    <div className="py-3 px-4 bg-green-50 rounded-lg border border-green-200">
                      <h4 className="text-sm font-semibold text-green-800 mb-2 flex items-center space-x-2">
                        <FaCheckCircle className="text-green-600" />
                        <span>Approval Details</span>
                      </h4>
                      <div className="space-y-1">
                        <div className="flex justify-between">
                          <span className="text-sm text-green-700">Approved Date:</span>
                          <span className="text-sm font-medium text-green-900">
                            {formatDate(selectedContract.approved_at)}
                          </span>
                        </div>
                        {selectedContract.approved_by && (
                          <div className="flex justify-between">
                            <span className="text-sm text-green-700">Approved By:</span>
                            <span className="text-sm font-medium text-green-900">
                              User ID: {selectedContract.approved_by}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Rejection Details */}
                  {selectedContract.is_rejected && (
                    <div className="py-3 px-4 bg-red-50 rounded-lg border border-red-200">
                      <h4 className="text-sm font-semibold text-red-800 mb-2 flex items-center space-x-2">
                        <FaTimesCircle className="text-red-600" />
                        <span>Rejection Details</span>
                      </h4>
                      <div className="space-y-2">
                        {selectedContract.rejection_reason && (
                          <div>
                            <div className="text-sm font-medium text-red-700 mb-1">Reason:</div>
                            <div className="text-sm text-red-900 bg-red-100 p-3 rounded">
                              {selectedContract.rejection_reason}
                            </div>
                          </div>
                        )}
                        {selectedContract.rejected_at && (
                          <div className="flex justify-between">
                            <span className="text-sm text-red-700">Rejected Date:</span>
                            <span className="text-sm font-medium text-red-900">
                              {formatDate(selectedContract.rejected_at)}
                            </span>
                          </div>
                        )}
                        {selectedContract.rejected_by && (
                          <div className="flex justify-between">
                            <span className="text-sm text-red-700">Rejected By:</span>
                            <span className="text-sm font-medium text-red-900">
                              User ID: {selectedContract.rejected_by}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Contract Timeline */}
                  <div className="pt-3 border-t border-gray-200">
                    <h4 className="text-sm font-semibold text-gray-700 mb-3">Contract Timeline</h4>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                          <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                          <span className="text-sm text-gray-600">Created</span>
                        </div>
                        <span className="text-sm text-gray-900">
                          {formatDate(selectedContract.createdDate)}
                        </span>
                      </div>

                      {selectedContract.updatedDate && (
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-2">
                            <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                            <span className="text-sm text-gray-600">Last Updated</span>
                          </div>
                          <span className="text-sm text-gray-900">
                            {formatDate(selectedContract.updatedDate)}
                          </span>
                        </div>
                      )}

                      {selectedContract.approved_at && (
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-2">
                            <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                            <span className="text-sm text-gray-600">Approved</span>
                          </div>
                          <span className="text-sm text-gray-900">
                            {formatDate(selectedContract.approved_at)}
                          </span>
                        </div>
                      )}

                      {selectedContract.rejected_at && (
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-2">
                            <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                            <span className="text-sm text-gray-600">Rejected</span>
                          </div>
                          <span className="text-sm text-gray-900">
                            {formatDate(selectedContract.rejected_at)}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Financial Information */}
              <div className="bg-white p-6 rounded-xl border border-gray-200">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-lg font-semibold text-gray-900">Pricing & Discounts</h3>
                  <span className="text-sm text-gray-500 bg-gray-100 px-3 py-1 rounded-full">
                    Contract Details
                  </span>
                </div>

                {/* Main Pricing Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                  {/* Rate Type Card */}
                  <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                    <div className="flex items-center space-x-2 mb-2">
                      <FaHotel className="text-gray-600 text-sm" />
                      <span className="text-sm font-medium text-gray-700">Rate Type</span>
                    </div>
                    <div className="text-lg font-bold text-gray-900 capitalize">
                      {selectedContract.contract_discount_structure?.[0]?.rate_type || 'corporate'}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">Corporate pricing applied</div>
                  </div>

                  {/* Discount Card */}
                  <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                    <div className="flex items-center space-x-2 mb-2">
                      <FaPercentage className="text-green-600 text-sm" />
                      <span className="text-sm font-medium text-gray-700">Discount</span>
                    </div>
                    {selectedContract.contract_discount_structure?.[0]?.discount_type === 'flat' &&
                    selectedContract.contract_discount_structure?.[0]?.flat_discount &&
                    selectedContract.contract_discount_structure[0].flat_discount !== '0' ? (
                      <>
                        <div className="text-lg font-bold text-green-600">
                          ₹
                          {parseInt(
                            selectedContract.contract_discount_structure[0].flat_discount
                          ).toLocaleString()}
                        </div>
                        <div className="text-xs text-gray-500 mt-1">Flat discount amount</div>
                      </>
                    ) : selectedContract.contract_discount_slab &&
                      selectedContract.contract_discount_slab.length > 0 &&
                      selectedContract.contract_discount_slab.some(
                        (slab) => parseFloat(slab.discount_percent) > 0
                      ) ? (
                      <>
                        <div className="text-lg font-bold text-blue-600">
                          {Math.max(
                            ...selectedContract.contract_discount_slab.map((s) =>
                              parseFloat(s.discount_percent)
                            )
                          )}
                          %
                        </div>
                        <div className="text-xs text-gray-500 mt-1">Maximum discount</div>
                      </>
                    ) : (
                      <>
                        <div className="text-lg font-bold text-gray-500">0%</div>
                        <div className="text-xs text-gray-500 mt-1">No discount applied</div>
                      </>
                    )}
                  </div>

                  {/* Credit Limit Card */}
                  <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                    <div className="flex items-center space-x-2 mb-2">
                      <FaMoneyBillWave className="text-gray-600 text-sm" />
                      <span className="text-sm font-medium text-gray-700">Credit Limit</span>
                    </div>
                    <div className="text-lg font-bold text-gray-900">
                      ₹
                      {selectedContract.contract_discount_structure?.[0]?.credit_limit
                        ? parseInt(
                            selectedContract.contract_discount_structure[0].credit_limit
                          ).toLocaleString()
                        : '0'}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">Maximum credit allowed</div>
                  </div>

                  {/* Payment Terms Card */}
                  <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                    <div className="flex items-center space-x-2 mb-2">
                      <FaReceipt className="text-gray-600 text-sm" />
                      <span className="text-sm font-medium text-gray-700">Payment Terms</span>
                    </div>
                    <div className="text-lg font-bold text-gray-900">
                      {selectedContract.contract_discount_structure?.[0]?.payment_terms
                        ? selectedContract.contract_discount_structure[0].payment_terms.replace(
                            'net',
                            'Net '
                          )
                        : 'Net 30'}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">Payment due days</div>
                  </div>
                </div>

                {/* Additional Details Grid */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                  {/* Base Price */}
                  {selectedContract.contract_discount_structure?.[0]?.base_price &&
                    selectedContract.contract_discount_structure[0].base_price !== '0' && (
                      <div className="bg-blue-50 p-4 rounded-lg border border-blue-100">
                        <div className="flex items-center space-x-2 mb-2">
                          <span className="text-sm font-medium text-blue-700">Base Price</span>
                        </div>
                        <div className="text-lg font-bold text-blue-900">
                          ₹
                          {parseInt(
                            selectedContract.contract_discount_structure[0].base_price
                          ).toLocaleString()}
                        </div>
                        <div className="text-xs text-blue-600 mt-1">Starting room rate</div>
                      </div>
                    )}

                  {/* Late Payment Fee */}
                  {selectedContract.contract_discount_structure?.[0]?.late_payment_fee &&
                    selectedContract.contract_discount_structure[0].late_payment_fee !== '0' && (
                      <div className="bg-red-50 p-4 rounded-lg border border-red-100">
                        <div className="flex items-center space-x-2 mb-2">
                          <span className="text-sm font-medium text-red-700">Late Payment Fee</span>
                        </div>
                        <div className="text-lg font-bold text-red-900">
                          ₹
                          {parseInt(
                            selectedContract.contract_discount_structure[0].late_payment_fee
                          ).toLocaleString()}
                        </div>
                        <div className="text-xs text-red-600 mt-1">
                          Additional charge for late payments
                        </div>
                      </div>
                    )}
                  {selectedContract.contract_duration?.[0]?.withdrawal_penalty &&
                    selectedContract.contract_duration[0].withdrawal_penalty !== '0' && (
                      <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                        <div className="flex items-center space-x-2 mb-2">
                          <span className="text-sm font-medium text-gray-700">
                            Withdrawal Penalty
                          </span>
                        </div>
                        <div className="text-lg font-bold text-gray-900">
                          {parseInt(
                            selectedContract.contract_duration[0].withdrawal_penalty
                          ).toLocaleString()}
                          %
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                          Penalty charged for withdrawing contract early
                        </div>
                      </div>
                    )}

                  {/* Currency */}
                  <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                    <div className="flex items-center space-x-2 mb-2">
                      <span className="text-sm font-medium text-gray-700">Currency</span>
                    </div>
                    <div className="text-lg font-bold text-gray-900">
                      {selectedContract.contract_discount_structure?.[0]?.currency || 'INR'}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">Transaction currency</div>
                  </div>
                </div>

                {/* Discount Slabs Section - ONLY SHOW IF THERE ARE SLABS WITH DISCOUNT > 0 */}
                {selectedContract.contract_discount_slab &&
                  selectedContract.contract_discount_slab.length > 0 &&
                  selectedContract.contract_discount_slab.some(
                    (slab) => parseFloat(slab.discount_percent) > 0
                  ) && (
                    <div className="mb-8">
                      <div className="flex items-center justify-between mb-4">
                        <h4 className="text-md font-semibold text-gray-900">Discount Slabs</h4>
                        <span className="text-sm text-gray-500 bg-gray-100 px-3 py-1 rounded-full">
                          {
                            selectedContract.contract_discount_slab.filter(
                              (slab) => parseFloat(slab.discount_percent) > 0
                            ).length
                          }
                          tier
                          {selectedContract.contract_discount_slab.filter(
                            (slab) => parseFloat(slab.discount_percent) > 0
                          ).length > 1
                            ? 's'
                            : ''}
                        </span>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {selectedContract.contract_discount_slab
                          .filter((slab) => parseFloat(slab.discount_percent) > 0) // Only show slabs with discount > 0
                          .sort((a, b) => parseFloat(a.min_amount) - parseFloat(b.min_amount))
                          .map((slab, index) => (
                            <div
                              key={index}
                              className="p-4 rounded-lg border bg-green-50 border-green-200"
                            >
                              <div className="flex items-center justify-between mb-3">
                                <div className="flex items-center space-x-2">
                                  <div className="w-8 h-8 rounded-full flex items-center justify-center bg-green-100 text-green-800">
                                    {index + 1}
                                  </div>
                                  <span className="text-sm font-medium text-gray-700">
                                    {parseFloat(slab.min_amount) === 0
                                      ? 'Base Rate'
                                      : `Above ₹${parseFloat(slab.min_amount).toLocaleString()}`}
                                  </span>
                                </div>
                                <span
                                  className={`text-md font-bold ${parseFloat(slab.discount_percent) >= 20 ? 'text-green-600' : 'text-blue-600'}`}
                                >
                                  {parseFloat(slab.discount_percent)}% OFF
                                </span>
                              </div>

                              <div className="space-y-2">
                                <div className="flex justify-between items-center">
                                  <span className="text-sm text-gray-600">Minimum Booking:</span>
                                  <span className="text-sm font-semibold text-gray-900">
                                    ₹{parseFloat(slab.min_amount).toLocaleString()}
                                  </span>
                                </div>

                                <div className="flex justify-between items-center">
                                  <span className="text-sm text-gray-600">Discount Rate:</span>
                                  <span className="text-sm font-semibold text-green-600">
                                    {parseFloat(slab.discount_percent)}%
                                  </span>
                                </div>
                              </div>
                            </div>
                          ))}
                      </div>
                    </div>
                  )}
              </div>
              {/* PDF Status */}
              {selectedContract.hasPdf && (
                <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-6 rounded-2xl border border-blue-200 shadow-sm">
                  <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">
                        Contract Document
                      </h3>
                      <div className="flex items-center space-x-4">
                        <div className="inline-flex items-center space-x-2 px-4 py-2 rounded-full bg-green-100 text-green-800">
                          <FaFilePdf className="text-sm" />
                          <span className="text-sm font-medium">
                            {selectedContract.isSigned
                              ? 'Signed Contract Available'
                              : 'Contract PDF Available'}
                          </span>
                        </div>
                        <span className="text-sm text-gray-500">
                          {selectedContract.contractPdf?.split('/').pop()}
                        </span>
                      </div>
                    </div>
                    <button
                      onClick={() => handleDownloadContract(selectedContract)}
                      className="px-6 py-3 bg-gray-900 text-white rounded-xl hover:bg-black transition-all duration-200 font-semibold flex items-center space-x-2 shadow-lg hover:shadow-xl transform hover:scale-105"
                    >
                      <FaDownload size={16} />
                      <span>Download PDF</span>
                    </button>
                  </div>
                </div>
              )}

              {/* Terms and Conditions */}
              {selectedContract.termsAndConditions && (
                <div className="bg-gradient-to-br from-gray-50 to-white p-6 rounded-2xl border border-gray-100 shadow-sm">
                  <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center space-x-2">
                    <div className="w-2 h-2 bg-gray-900 rounded-full"></div>
                    <span>Terms & Conditions</span>
                  </h3>
                  <div className="prose prose-sm max-w-none">
                    <pre className="whitespace-pre-wrap font-sans text-gray-700 text-sm">
                      {selectedContract.termsAndConditions}
                    </pre>
                  </div>
                </div>
              )}
            </div>

            {/* Footer Actions */}
            <div className="flex justify-end space-x-4 pt-8 mt-8 border-t border-gray-100">
              <button
                onClick={() => setShowViewModal(false)}
                className="px-8 py-3 bg-gray-900 text-white rounded-xl hover:bg-black transition-all duration-200 font-semibold shadow-lg hover:shadow-xl transform hover:scale-105"
              >
                Close Details
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Delete Confirmation Modal */}
      {showDeleteModal && selectedContract && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white p-8 rounded-xl shadow-2xl max-w-lg w-full mx-4">
            <div className="flex items-center space-x-3 mb-4">
              <FaTrash className="text-red-600 text-xl" />
              <h2 className="text-xl font-semibold text-black">Delete Contract</h2>
            </div>
            <p className="text-black mb-2">
              Are you sure you want to delete the contract{' '}
              <span className="font-semibold text-black">"{selectedContract.contractname}"</span>?
            </p>
            <p className="text-sm text-black mb-6">
              Contract ID: <span className="font-mono">{selectedContract.contractid}</span>
            </p>
            <p className="text-sm text-red-600 font-semibold mb-6">
              This action cannot be undone and will permanently remove the contract data.
            </p>
            <div className="flex justify-end space-x-4">
              <button
                type="button"
                className="px-6 py-2.5 border border-gray-300 text-black rounded-lg hover:bg-gray-50 transition duration-200 font-semibold"
                onClick={() => setShowDeleteModal(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="px-6 py-2.5 bg-red-600 text-white rounded-lg hover:bg-red-700 transition duration-200 font-semibold"
                onClick={() => {
                  setContracts((prev) =>
                    prev.filter((contract) => contract.contractid !== selectedContract.contractid)
                  );
                  setShowDeleteModal(false);
                  setSelectedContract(null);
                  showToast('Contract deleted successfully!', 'success');
                }}
              >
                Delete Contract
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CorporateContractsPage;
